import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSuperAdmin } from "../../context/SuperAdminContext";

const TOP_TABS = [
  { key: "edit", label: "Status" },
  { key: "history", label: "History" },
];

const CATEGORY_OPTIONS = [
  { key: "onboarding", label: "Onboarding" },
  { key: "refcheck", label: "Refcheck" },
  { key: "retention", label: "Retention" },
  { key: "companyScreening", label: "Company Screening" },
  { key: "eshram", label: "E-Shram" },
];


const HISTORY_FILTER_OPTIONS = [{ key: "", label: "All" }, ...CATEGORY_OPTIONS];


const EMPTY_TAB_STATE = {
  status: "",
  subStatus: "",
  remarks: "",
  issues: [],
  companyId: "",
  companyName: "",
};

const LastCallStatusModal = ({
  isOpen,
  onClose,
  worker,
  onUpdate,
  statusOptions = [], 
  subStatusOptions = {},
}) => {
  const {
    fetchLastCallStatuses,
    fetchLastCallSubStatuses,
    updateWorkerVerificationStatus,
    fetchWorkerVerificationHistory,
    fetchActiveCompaniesByLocation,
    fetchRetentionIssues,
    fetchActiveLocations,
    fetchCompaniesByLocations
  } = useSuperAdmin();

  const [topTab, setTopTab] = useState("edit");

  // Which category is currently selected for editing (was "activeTab")
  const [activeTab, setActiveTab] = useState("onboarding");

  // Per-category form state, keyed by category key
  const [tabData, setTabData] = useState({
    onboarding: { ...EMPTY_TAB_STATE },
    refcheck: { ...EMPTY_TAB_STATE },
    retention: { ...EMPTY_TAB_STATE },
    companyScreening: { ...EMPTY_TAB_STATE },
    eshram: { ...EMPTY_TAB_STATE },
    
  });

  const [submitting, setSubmitting] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableSubStatuses, setAvailableSubStatuses] = useState([]);
  const [subStatusLoading, setSubStatusLoading] = useState(false);
  const [ISSUE_GROUPS, setISSUE_GROUPS] = useState([])
  

  // ── Company Screening: active companies for the worker's location ──────
const [locations, setLocations] = useState([]);
const [locationsLoading, setLocationsLoading] = useState(false);
const [companies, setCompanies] = useState([]);
const [companiesLoading, setCompaniesLoading] = useState(false);

  // ── History state (History tab — shows every category, filterable) ──────
  const [historyFilter, setHistoryFilter] = useState(""); // "" = All
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  const current = tabData[activeTab];

  const updateCurrent = (patch) => {
    setTabData((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], ...patch },
    }));
  };


useEffect(() => {
  if (!isOpen || activeTab !== "companyScreening") return;

  const loadLocations = async () => {
    setLocationsLoading(true);

    try {
      const response = await fetchActiveLocations();

      if (response?.success && Array.isArray(response?.locations)) {
        setLocations(response.locations);
      } else {
        setLocations([]);
      }
    } catch (err) {
      console.error("Failed to load locations:", err);
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  loadLocations();
}, [isOpen, activeTab, fetchActiveLocations]);


useEffect(() => {
  if (!current.locationId) {
    setCompanies([]);
    return;
  }

  const loadCompanies = async () => {
    setCompaniesLoading(true);

    try {
      const response = await fetchCompaniesByLocations([
        current.locationId,
      ]);

      const list =
        response?.success && Array.isArray(response?.companies)
          ? response.companies
          : [];

      setCompanies(list);
    } catch (err) {
      console.error("Failed to load companies:", err);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  loadCompanies();
}, [current.locationId, fetchCompaniesByLocations]);

  // ── Fetch status options from API via context ──────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // If parent passed options, use them
    // const filtered = statusOptions.filter((opt) => opt !== "All");
    // if (filtered.length > 0) {
    //   setAvailableStatuses(filtered);
    //   return;
    // }

    const loadStatuses = async () => {
      try {
        const response = await fetchLastCallStatuses(activeTab);

        let list = [];
        if (response?.success && Array.isArray(response.statuses)) {
          list = response.statuses;
        } else if (Array.isArray(response?.data)) {
          list = response.data;
        } else if (Array.isArray(response)) {
          list = response;
        }

        const extracted = list
          .map((item) =>
            typeof item === "object" && item !== null
              ? item.label || item.key || item.name || item.status
              : item
          )
          .filter(Boolean);

        setAvailableStatuses(extracted);
      } catch (err) {
        console.error("Failed to load status options in modal:", err);
        toast.error("Failed to load status options");
      }
    };

    loadStatuses();
  }, [isOpen, activeTab, statusOptions, fetchLastCallStatuses]);

  // ── Fetch sub-status options whenever status changes ───────────────────────
  useEffect(() => {
    if (!isOpen || !current.status) {
      setAvailableSubStatuses([]);
      return;
    }

    // If parent passed a mapping, use it
    if (subStatusOptions[current.status]?.length) {
      setAvailableSubStatuses(subStatusOptions[current.status]);
      return;
    }

    if (!fetchLastCallSubStatuses) {
      setAvailableSubStatuses([]);
      return;
    }

    const loadSubStatuses = async () => {
      setSubStatusLoading(true);
      try {
        const response = await fetchLastCallSubStatuses(activeTab, current.status);

        let list = [];
        if (response?.success && Array.isArray(response.subStatuses)) {
          list = response.subStatuses;
        } else if (Array.isArray(response?.data)) {
          list = response.data;
        } else if (Array.isArray(response)) {
          list = response;
        }

        const extracted = list
          .map((item) =>
            typeof item === "object" && item !== null
              ? item.label || item.key || item.name || item.subStatus
              : item
          )
          .filter(Boolean);

        setAvailableSubStatuses(extracted);
      } catch (err) {
        console.error("Failed to load sub-status options:", err);
        setAvailableSubStatuses([]);
      } finally {
        setSubStatusLoading(false);
      }
    };

    loadSubStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, current.status]);

  // ── Fetch active companies for the worker's location (Company Screening) ──
//   useEffect(() => {
//     if (!isOpen || activeTab !== "companyScreening") return;

//     if ( !fetchActiveCompaniesByLocation) {
//       setCompanies([]);
//       return;
//     }

// const loadCompanies = async () => {
//   setCompaniesLoading(true);

//   try {
//     const response = await fetchActiveCompaniesByLocation("all", true);

//  const list = response?.success && Array.isArray(response?.companies)
//   ? response.companies
//   : [];

// setCompanies(list);
//   } catch (err) {
//     console.error("Failed to load companies by location:", err);
//     setCompanies([]);
//   } finally {
//     setCompaniesLoading(false);
//   }
// };
//     loadCompanies();
//   }, [isOpen, activeTab, worker?.locationId, fetchActiveCompaniesByLocation]);

  // ── Prefill form from worker on open ────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !worker) return;

    setTopTab("edit");
    setActiveTab("onboarding");
    setHistoryFilter("");
    setHistory([]);
    setTabData({
      onboarding: {
        status: worker.onboardingStatus?.status ?? "",
        subStatus: worker.onboardingStatus?.subStatus ?? "",
        remarks: worker.onboardingStatus?.remarks ?? "",
        issues: worker.onboardingStatus?.issues ?? [],
        companyId: "",
        companyName: "",
      },
      refcheck: {
        status: worker.refcheckStatus?.status ?? "",
        subStatus: worker.refcheckStatus?.subStatus ?? "",
        remarks: worker.refcheckStatus?.remarks ?? "",
        issues: worker.refcheckStatus?.issues ?? [],
        companyId: "",
        companyName: "",
      },
      retention: {
        status: worker.retentionStatus?.status ?? "",
        subStatus: worker.retentionStatus?.subStatus ?? "",
        remarks: worker.retentionStatus?.remarks ?? "",
        issues: worker.retentionStatus?.issues ?? [],
        companyId: "",
        companyName: "",
      },
      companyScreening: {
        status: worker.companyScreeningStatus?.status ?? "",
        subStatus: worker.companyScreeningStatus?.subStatus ?? "",
        remarks: worker.companyScreeningStatus?.remarks ?? "",
        issues: worker.companyScreeningStatus?.issues ?? [],
        companyId: worker.companyScreeningStatus?.companyId ?? "",
        companyName: worker.companyScreeningStatus?.companyName ?? "",
      },
      eshram: {
        status: worker.eshram?.status ?? "",
        subStatus: worker.eshram?.subStatus ?? "",
        remarks: worker.eshram?.remarks ?? "",
        issues: worker.eshram?.issues ?? [],
        companyId: "",
        companyName: "",
      },
    });
  }, [isOpen, worker]);

  // ── Load history — shows ALL categories by default, or one category ────────
  // The filter never slices the response client-side; it only decides
  // whether "category" is sent to the endpoint at all.
  const loadHistory = async (filterOverride) => {
    if (!worker?.workerId) return;
    const filterToUse = filterOverride !== undefined ? filterOverride : historyFilter;

    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const params = filterToUse ? { category: filterToUse } : {};
      const response = await fetchWorkerVerificationHistory(worker.workerId, worker.lastCompanyName,params);

      let list = [];
      if (response?.success && Array.isArray(response.data)) {
        list = response.data;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
      } else if (Array.isArray(response)) {
        list = response;
      }

      setHistory(list);
    } catch (error) {
      console.error("Failed to load verification history:", error);
      setHistoryError(true);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

useEffect(() => {
  if (!isOpen || activeTab !== "retention") return;

  if (!fetchRetentionIssues) {
    setISSUE_GROUPS([]);
    return;
  }

  const loadRetentionIssues = async () => {
    try {
      const response = await fetchRetentionIssues();

      let list = [];
      if (response?.success && Array.isArray(response.data)) {
        list = response.data;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
      } else if (Array.isArray(response)) {
        list = response;
      }

      setISSUE_GROUPS(list);
    } catch (error) {
      console.error("Failed to fetch retention issues:", error);
      setISSUE_GROUPS([]);
    }
  };

  loadRetentionIssues();
}, [isOpen, activeTab, fetchRetentionIssues]);

  // Refetch history whenever the modal opens, the worker changes, or the filter changes.
  useEffect(() => {
    if (topTab === "history") {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topTab, historyFilter, worker?.workerId]);

  // ── Toggle an issue chip on/off ─────────────────────────────────────────
  const toggleIssue = (issueKey) => {
    const issues = current.issues.includes(issueKey)
      ? current.issues.filter((i) => i !== issueKey)
      : [...current.issues, issueKey];
    updateCurrent({ issues });
  };
  // Flat lookup: issue key -> its group heading
const ISSUE_KEY_TO_HEADING = ISSUE_GROUPS.reduce((map, group) => {
  group.options.forEach((opt) => {
    map[opt.key] = group.heading;
  });
  return map;
}, {});

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!current.status) {
      toast.error("Please select a status");
      return;
    }

    if (activeTab === "companyScreening" && !current.companyId) {
      toast.error("Please select a company");
      return;
    }

setSubmitting(true);
try {
  // Issue Type is only collected/shown for the Retentions category —
  // don't send stale issues left over from another category's state.
  // Build { heading: [selectedKeys...] }, only including groups with a selection.


    const toCamelCase = (str) => {
  return str
    .replace(/[/\s]+(.)/g, (_, char) => char.toUpperCase())
    .replace(/^./, (char) => char.toLowerCase());
};

const issuesForPayload =
  activeTab === "retention"
    ? ISSUE_GROUPS.reduce((acc, group) => {
        const selectedLabels = group.options
          .filter((opt) => current.issues.includes(opt.key))
          .map((opt) => opt.label);

        acc[toCamelCase(group.heading)] = selectedLabels;

        return acc;
      }, {})
    : {};

  const response = await updateWorkerVerificationStatus(worker.workerId, {
    category: activeTab,
    status: current.status,
    subStatus: current.subStatus,
    remarks: current.remarks,
 ...issuesForPayload,
 ...(activeTab === "retention" &&
    issuesForPayload.factoryIssues?.length > 0 && {
      lastCompanyName: worker.lastCompanyName,
      lastCompanyId: worker.lastCompanyId,
    }),
    ...(activeTab === "companyScreening" && {
      lastCompanyId: current.companyId,
      lastCompanyName: current.companyName,
    }),
  });

  if (response?.success) {
    toast.success("Status updated successfully");
  onUpdate(
  worker.workerId,
  response.data.verification.lastStatus.status,
  response.data.verification.lastStatus.remarks
);
    // Refresh history right away so a Save is reflected without reopening
    loadHistory();
    onClose();
  } else {
    toast.error(response?.message || "Failed to update status");
  }
} catch (error) {
  console.error("Failed to update verification status:", error);
  toast.error(error?.response?.data?.message || "Failed to update status. Please try again.");
} finally {
  setSubmitting(false);
}
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  // "08 May 26 | 10:34" — matches the numbered history card format
  const formatHistoryDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    const datePart = d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
    const timePart = d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${datePart} | ${timePart}`;
  };

  const categoryLabel = (key) =>
    CATEGORY_OPTIONS.find((c) => c.key === key)?.label ?? key ?? "—";

  if (!isOpen) return null;

  const canSubmit =
    !submitting && 
    !!current.status && !!current.subStatus && 
    availableStatuses.length > 0 &&
    (activeTab !== "companyScreening" || !!current.companyId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black opacity-50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Call Status
              </h3>
              <p className="text-sm text-gray-500 capitalize mt-0.5">
                {worker?.name || "Worker"} · {worker?.phone || ""}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Top-level tabs: Edit Status / History */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {TOP_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setTopTab(tab.key)}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    topTab === tab.key
                      ? "border-b-2 border-indigo-500 text-indigo-600"
                      : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* ── EDIT STATUS TAB ─────────────────────────────────────────────── */}
          {topTab === "edit" && (
            <>
              <div className="p-6">
                <div className="space-y-4">
                  {/* Category radio buttons */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <div className="flex flex-wrap gap-4">
                      {CATEGORY_OPTIONS.map((cat) => (
                        <label
                          key={cat.key}
                          className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="category"
                            value={cat.key}
                            checked={activeTab === cat.key}
                            onChange={() => setActiveTab(cat.key)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          />
                          {cat.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Company select — Company Screening category only */}
{activeTab === "companyScreening" && (
  <div className="grid grid-cols-2 gap-3">

    {/* Location */}
<div>
  <label className="block text-xs font-medium text-gray-700 mb-1">
    Location <span className="text-red-500">*</span>
  </label>

  <select
    value={current.locationId || ""}
    onChange={(e) => {
      const selectedId = e.target.value;

      updateCurrent({
        locationId: selectedId,
        companyId: "",
        companyName: "",
      });
    }}
    disabled={locationsLoading}
    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs disabled:bg-gray-100 disabled:cursor-not-allowed"
  >
    <option value="">
      {locationsLoading ? "Loading..." : "Select location"}
    </option>

    {locations.map((location) => (
      <option key={location._id} value={location._id}>
        {location.name} ({location.locationCode})
      </option>
    ))}
  </select>
</div>

    {/* Company */}
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        Company <span className="text-red-500">*</span>
      </label>

      <select
        value={current.companyId || ""}
        onChange={(e) => {
          const selectedId = e.target.value;

          const selected = companies.find(
            (c) => (c._id || c.id) === selectedId
          );

          updateCurrent({
            companyId: selectedId,
            companyName: selected?.name || "",
          });
        }}
        disabled={!current.locationId || companiesLoading}
        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <option value="">
          {!current.locationId
            ? "Select location first"
            : companiesLoading
            ? "Loading..."
            : "Select company"}
        </option>

        {companies.map((company) => (
          <option
            key={company._id || company.id}
            value={company._id || company.id}
          >
            {company.name} ({company.companyCode})
          </option>
        ))}
      </select>
    </div>

  </div>
)}

                  {/* Status  sub-status select */}
<div className="grid grid-cols-2 gap-3">
  {/* Status */}
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      Status <span className="text-red-500">*</span>
    </label>

    <select
      value={current.status}
      onChange={(e) =>
        updateCurrent({ status: e.target.value, subStatus: "" })
      }
      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
    >
      <option value="">Select status</option>

      {availableStatuses.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>

  {/* Sub-Status */}
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      Sub-Status <span className="text-red-500">*</span>
    </label>

    <select
      value={current.subStatus}
      onChange={(e) => updateCurrent({ subStatus: e.target.value })}
      disabled={!current.status || subStatusLoading}
      required
      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs disabled:bg-gray-100 disabled:cursor-not-allowed"
    >
      <option value="">
        {subStatusLoading ? "Loading..." : "Select Sub-Status"}
      </option>

      {availableSubStatuses.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
</div>
                  {/* Remarks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remarks <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={current.remarks}
                      onChange={(e) => updateCurrent({ remarks: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                      placeholder="Add any remarks about the call..."
                    />
                  </div>

                  {/* Issue Type — shown after Remarks, Retentions category only */}
{activeTab === "retention" && (
  <div className="">


<div className="space-y-3">
  {ISSUE_GROUPS.map((group) => (
    <div key={group.heading}>
      {/* Heading */}
      <h3 className="text-xs font-semibold text-gray-700 mb-1.5">
        {group.heading}
        {group.heading === "Client / Factory Issues" && worker.lastCompanyName && (
          <span className="text-gray-700 font-semibold ml-1">
            ({worker.lastCompanyName})
          </span>
        )}
      </h3>

      {/* Options */}
      <div className="flex flex-wrap gap-1.5">
        {group.options.map((issue) => {
          const selected = current.issues.includes(issue.key);

          return (
            <button
              key={issue.key}
              type="button"
              onClick={() => toggleIssue(issue.key)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium transition-colors ${
                selected
                  ? "bg-indigo-100 border-indigo-400 text-indigo-700"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {selected ? (
                <svg
                  className="h-3 w-3 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="h-3 w-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              )}

              {issue.label}
            </button>
          );
        })}
      </div>
    </div>
  ))}
</div>
  </div>
)}
                </div>
              </div>

              {/* Save button */}
              <div className="px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </>
          )}

          {/* ── HISTORY TAB ──────────────────────────────────────────────────── */}
          {topTab === "history" && (
            <div className="px-6 py-4 space-y-4">
              {/* Small category filter — only changes what's sent to the endpoint */}
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">
  {CATEGORY_OPTIONS.find((item) => item.key === historyFilter)?.label || historyFilter} History
</p>
                <select
                  value={historyFilter}
                  onChange={(e) => setHistoryFilter(e.target.value)}
                  className="px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {HISTORY_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.key || "all"} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {historyLoading ? (
                <div className="flex justify-center py-6">
                  <svg className="animate-spin h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                </div>
              ) : historyError ? (
                <div className="text-center py-4">
                  <p className="text-red-500 text-sm mb-2">Failed to load history.</p>
                  <button
                    onClick={() => loadHistory()}
                    className="text-indigo-600 text-sm underline hover:text-indigo-800"
                  >
                    Retry
                  </button>
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm border border-gray-200 rounded-lg">
                  No history available for this worker.
                </div>
              ) : (
                <div className="space-y-3 pr-0.5">
                  {history.map((record, index) => (
<div
  key={record._id ?? index}
  className="bg-gray-50 rounded-lg px-4 py-3 flex gap-3"
>
  <div className="flex-1 min-w-0 gap-y-2">
    {/* Meta row: date/time · created by · category · status · sub status */}
    <div className="text-xs text-gray-500">
       {record.category && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-indigo-50 to-violet-50 text-indigo-700 border border-indigo-100 font-medium text-[10px] leading-none tracking-wide uppercase">
          <span className="w-1 h-1 rounded-full bg-indigo-500" />
          {categoryLabel(record.category)}
        </span>
      )}
    </div>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-2">
      <span>
        On {formatHistoryDateTime(record.createdAt ?? record.updatedAt)}
      </span>
      {record.updatedBy && (
        <span>
          Created by:{" "}
          <span className="text-gray-800 font-medium">
            {typeof record.updatedBy === "object"
              ? record.updatedBy.name || record.updatedBy.email || "—"
              : record.updatedBy}
          </span>
        </span>
      )}
      {record.status && (
        <span>
          Status:{" "}
          <span className="text-gray-800 font-medium">
            {record.status}
          </span>
        </span>
      )}
      {record.subStatus && (
        <span>
          Sub Status:{" "}
          <span className="text-gray-800 font-medium">
            {record.subStatus}
          </span>
        </span>
      )}
      {record.lastCompanyName && record.category=="retention" && (
        <span>
          Last Company:{" "}
          <span className="text-gray-800 font-medium">
            {record.lastCompanyName}
          </span>
        </span>
      )}

            {record.lastCompanyName && record.category=="companyScreening" && (
        <span>
          Company Selected:{" "}
          <span className="text-gray-800 font-medium">
            {record.lastCompanyName}
          </span>
        </span>
      )}
    </div>

    {/* Remarks */}
    {record.remarks && (
      <div className="flex items-start gap-2 mt-1">
        <p className="text-sm text-gray-500 whitespace-nowrap">
          Remarks:
        </p>
        <p className="font-medium text-sm text-gray-800">
          {record.remarks}
        </p>
      </div>
    )}

{(() => {
  const issueGroups = Object.entries(record || {}).filter(
    ([_, value]) => Array.isArray(value) && value.length > 0
  );

  return issueGroups.length > 0 ? (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {issueGroups.map(([heading, issues]) => {
        const formattedHeading = heading
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());

        return (
          <div key={heading} className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-800">
              {formattedHeading}:
            </span>

            {issues.map((issue, i) => (
              <span
                key={`${heading}-${issue}-${i}`}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100"
              >
                {issue}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  ) : null;
})()}
  </div>
</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LastCallStatusModal;